<template>
  <Dialog
    :visible="!!store.reapprovePrompt && !asksNothing"
    modal
    :header="t('tasks.reapprove.title')"
    :style="{ width: '32rem' }"
    :breakpoints="{ '960px': '90vw' }"
    :closable="!loading"
    @update:visible="onDismiss"
  >
    <!-- Three sentences, one slot. `source_deleted` is the one that reports a
         change rather than posing a question: the proposal this task came from
         is gone, so reviving it makes the task stand on its own from now on.
         The user did not do that and would otherwise meet it later, as a task
         whose source link goes nowhere. -->
    <p class="text-sm mb-4">
      {{ explainsOnly
        ? t('tasks.reapprove.source_deleted')
        : store.reapprovePrompt?.resetRefused
          ? t('tasks.reapprove.reset_unavailable')
          : t('tasks.reapprove.body') }}
    </p>
    <!-- A bulk accept over several parked rows asks ONCE and applies the answer
         to all of them (4.8a). Saying so is the difference between a choice and
         a surprise: the same two buttons decide ten tasks, not one. -->
    <p v-if="batchCount > 1" class="text-sm mb-4 font-medium">
      {{ t('tasks.reapprove.applies_to_all', { count: batchCount }) }}
    </p>

    <div class="flex flex-col gap-2">
      <Button
        :label="explainsOnly ? t('tasks.reapprove.confirm') : t('tasks.reapprove.keep')"
        icon="pi pi-check"
        severity="secondary"
        outlined
        :disabled="loading"
        @click="choose('keep')"
      />
      <Button
        v-if="!keepIsTheOnlyMode"
        :label="t('tasks.reapprove.reset')"
        icon="pi pi-replay"
        severity="secondary"
        outlined
        :disabled="loading"
        @click="choose('reset')"
      />
    </div>

    <template #footer>
      <Button
        :label="t('tasks.reapprove.cancel')"
        severity="secondary"
        text
        :disabled="loading"
        @click="onDismiss"
      />
    </template>
  </Dialog>
</template>

<script setup lang="ts">
/**
 * The keep-or-reset question a re-approval has to ask (IR-126 §5.4).
 *
 * **Neither button is pre-selected, and there is no default.** Re-approving a
 * task edited while it was sent back has two defensible answers, and choosing
 * silently either discards a human's work or approves edits they meant to
 * drop. `TaskReapproveCommand.content_mode` is required for the same reason.
 *
 * Mounted once in `App.vue` rather than per surface. Accept is offered from
 * four proposal surfaces, and a prompt mounted per surface is four chances to
 * forget one — the fan-out that MYAC-1003's review caught twice.
 */
import { computed, ref, watch } from 'vue'
import { useI18n } from 'vue-i18n'
import Dialog from 'primevue/dialog'
import Button from 'primevue/button'
import { useAssessmentsStore } from '@/stores/assessments'
import type { ContentMode } from '@/composables/useTaskCommands'

const { t } = useI18n()
const store = useAssessmentsStore()
const loading = ref(false)
/** How many tasks this one answer will revive — the row on screen plus its batch. */
const batchCount = computed(() => (store.reapprovePrompt ? 1 + store.reapproveQueue.length : 0))

/**
 * Whether this revival has a question in it at all (MYAC-1098).
 *
 * `keep` is the only mode a task without a proposal can be revived with —
 * there is no original to revert to — so for those the dialog stops being a
 * question. `none` answers itself; `deleted` still shows, because the task
 * changing character is a fact the user did not cause.
 */
const proposalState = computed(() => store.reapprovePrompt?.proposal ?? 'present')
const asksNothing = computed(() => proposalState.value === 'none')
const explainsOnly = computed(() => proposalState.value === 'deleted')
const keepIsTheOnlyMode = computed(
  () => asksNothing.value || explainsOnly.value || !!store.reapprovePrompt?.resetRefused,
)

/**
 * Answered already, by task id.
 *
 * A silent revival resolves the prompt it was raised for, and the store then
 * advances to the next queued one — so this watcher fires again, which is
 * correct. What it must not do is answer the SAME prompt twice: a refusal can
 * put one back on screen (that is what `resetRefused` does), and answering it
 * again unprompted would be an infinite exchange with a server that keeps
 * saying no.
 */
const answered = new Set<string>()

watch(
  () => (asksNothing.value ? (store.reapprovePrompt?.taskId ?? null) : null),
  (taskId) => {
    if (!taskId || answered.has(taskId)) return
    answered.add(taskId)
    void choose('keep')
  },
  { immediate: true },
)

async function choose(mode: ContentMode) {
  loading.value = true
  try {
    await store.resolveReapprove(mode)
  } catch {
    // The store has already reported it — a refusal opens its own toast, and
    // rethrowing here would surface a second, vaguer message for one failure.
  } finally {
    loading.value = false
  }
}

function onDismiss() {
  if (loading.value) return
  // Cancelling leaves the task parked, which is the state the user was already
  // in — so there is nothing to undo and nothing to warn about.
  void store.resolveReapprove(null)
}
</script>
